/**
 * Centralized localStorage key registry.
 * All keys in one place — prevents typos, makes cleanup auditable.
 */

// ── Key builders ────────────────────────────────────────────────────────────

/** Gantt start date for a specific flowchart (also stored in DB now; kept as fallback). */
export const lsGanttStartDate = (id: string) => `gantt-start-${id}` as const;

/** Workday start/end hour for a specific flowchart (also stored in DB now; kept as fallback). */
export const lsWorkDayHours = (id: string) => `gantt-workday-hours-${id}` as const;

/** "Hide non-working hours" Gantt display toggle for a specific flowchart (also stored in DB now; kept as fallback). */
export const lsHideNonWorkingHours = (id: string) => `gantt-hide-non-working-hours-${id}` as const;

/** "Hide non-working days" Gantt display toggle for a specific flowchart (also stored in DB now; kept as fallback). */
export const lsHideNonWorkingDays = (id: string) => `gantt-hide-non-working-days-${id}` as const;

/** "Only visible bars" Gantt export toggle for a specific flowchart (also stored in DB now; kept as fallback). */
export const lsOnlyVisibleBars = (id: string) => `gantt-only-visible-bars-${id}` as const;

/** Gantt "filter by resource" selection for a specific flowchart — a personal viewing
 *  preference, so localStorage-only (not synced to other collaborators via the DB). */
export const lsGanttResourceFilter = (id: string) => `gantt-resource-filter-${id}` as const;

/** "Filtered Export" mode toggle and its current (unsaved) row selection for a
 *  specific flowchart — localStorage-only, same convention as the resource filter
 *  above: a personal working state, not synced via the DB. Named export SELECTIONS
 *  saved under a name (gantt_export_row_presets) ARE DB-shared — see ExportRowPreset. */
export const lsFilteredExportEnabled = (id: string) => `gantt-filtered-export-enabled-${id}` as const;
export const lsExportSelectedBoxIds = (id: string) => `gantt-export-selected-box-ids-${id}` as const;

/** Gantt "filter by category" selection for a specific flowchart — a personal
 *  viewing preference, so localStorage-only (same convention as the resource filter
 *  above, not synced to other collaborators via the DB). */
export const lsGanttCategoryFilter = (id: string) => `gantt-category-filter-${id}` as const;

/** Gantt bar duration-label display unit (hours/days/weeks) for a specific flowchart
 *  (also stored in DB now; kept as fallback) — independent of the canvas's own
 *  scheduling duration unit, which it only defaults to on first use. */
export const lsGanttDurationDisplay = (id: string) => `gantt-duration-display-${id}` as const;

/** "Show subitems as indented rows" Gantt display toggle for a specific flowchart
 *  (also stored in DB now; kept as fallback). */
export const lsShowSubitemsInGantt = (id: string) => `gantt-show-subitems-${id}` as const;

/** "Show Area-based toplevel rows" Gantt display toggle for a specific flowchart
 *  (also stored in DB now; kept as fallback). */
export const lsShowAreaGroupsInGantt = (id: string) => `gantt-show-areas-${id}` as const;

/** "Show regular box rows" Gantt display toggle for a specific flowchart (also
 *  stored in DB now; kept as fallback) — turning this off with Area toplevel rows
 *  on shows only the toplevel bars. */
export const lsShowRegularBoxesInGantt = (id: string) => `gantt-show-regular-boxes-${id}` as const;

/** Canvas "View" menu toggles for a specific flowchart (also stored in DB now; kept
 *  as fallback) — canvas_-prefixed DB columns to stay unambiguous next to the
 *  separate Gantt "show Area toplevel" toggle above (a different feature). */
export const lsCanvasShowComments = (id: string) => `canvas-show-comments-${id}` as const;
export const lsCanvasShowSubitemBadges = (id: string) => `canvas-show-subitem-badges-${id}` as const;
export const lsCanvasShowUidBadges = (id: string) => `canvas-show-uid-badges-${id}` as const;
export const lsCanvasShowAreas = (id: string) => `canvas-show-areas-${id}` as const;

/** Mindmap: per-user priority/duration ordering for a specific mindmap. */
export const lsUserPriorities = (userId: string, id: string) => `mm-user-priorities-${userId}-${id}` as const;

/** Mindmap: per-user quadrant order for a specific mindmap. */
export const lsUserQOrder = (userId: string, id: string) => `mm-user-qorder-${userId}-${id}` as const;

/** Mindmap: day schedule order for a specific mindmap. */
export const lsDayOrder = (id: string) => `mm-dorder-${id}` as const;

/** Mindmap: non-working days for a specific mindmap. */
export const lsNonWorkingDays = (id: string) => `mm-nonworking-${id}` as const;

// ── Global (user-level) keys ─────────────────────────────────────────────────

export const LS_KEYS = {
  /** Which app mode was active last: 'flowchart' | 'mindmap' */
  homeMapType:        'home-map-type',
  /** ID of the flowchart to load on startup */
  flowchartDefaultId: 'flowchart-default-id',
  /** ID of the mindmap to load on startup */
  mindmapDefaultId:   'mindmap-default-id',
  /** Gantt: skip weekends in scheduling */
  ganttWorkdaysOnly:  'gantt-workdays-only',
  /** Gantt: use 24h instead of 8h per active day */
  ganttFullDay:       'gantt-full-day',
  /** Priority matrix quadrant labels */
  mmQuadrantLabels:   'mm-quadrant-labels',
  /** Priority matrix day column labels */
  mmDayLabels:        'mm-day-labels',
  /** Priority matrix: show schedule column */
  mmPmShowDays:       'mm-pm-show-days',
  /** Crash-recovery autosave for unsaved flowcharts */
  flowchartAutosave:  'flowchart-autosave',
  /** Crash-recovery autosave for unsaved mindmaps */
  mmAutosave:         'mm-autosave',
} as const;

export type LsKey = typeof LS_KEYS[keyof typeof LS_KEYS];

// ── Typed helpers ────────────────────────────────────────────────────────────

export function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {}
}

export function lsRemove(key: string): void {
  try { localStorage.removeItem(key); } catch {}
}

export function lsGetJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function lsSetJSON(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

/**
 * Remove all per-flowchart localStorage entries for a given ID.
 * Call when a flowchart is deleted.
 */
export function lsCleanupFlowchart(id: string): void {
  lsRemove(lsGanttStartDate(id));
  lsRemove(lsWorkDayHours(id));
  lsRemove(lsHideNonWorkingHours(id));
  lsRemove(lsHideNonWorkingDays(id));
  lsRemove(lsOnlyVisibleBars(id));
  lsRemove(lsGanttResourceFilter(id));
  lsRemove(lsFilteredExportEnabled(id));
  lsRemove(lsExportSelectedBoxIds(id));
  lsRemove(lsGanttCategoryFilter(id));
  lsRemove(lsGanttDurationDisplay(id));
  lsRemove(lsShowSubitemsInGantt(id));
  lsRemove(lsShowAreaGroupsInGantt(id));
  lsRemove(lsShowRegularBoxesInGantt(id));
  lsRemove(lsCanvasShowComments(id));
  lsRemove(lsCanvasShowSubitemBadges(id));
  lsRemove(lsCanvasShowUidBadges(id));
  lsRemove(lsCanvasShowAreas(id));
  lsRemove(lsDayOrder(id));
  lsRemove(lsNonWorkingDays(id));
}

/**
 * Remove all per-mindmap, per-user localStorage entries.
 * Call when a mindmap is deleted or a user logs out.
 */
export function lsCleanupMindmapUser(userId: string, id: string): void {
  lsRemove(lsUserPriorities(userId, id));
  lsRemove(lsUserQOrder(userId, id));
}
